import { Box, Divider, Stack } from '@mui/material';
import { DefinitionGrid, EmptyState, PageSections, SectionCard, StatusBadge, ValueBadge, designTokens } from '../../../design-system';
import type { WorkerRewards } from './model';
import { profileDateValue, profileTextValue } from './profileDisplay';

const contractTypeLabels: Record<string, string> = { PERMANENT: '무기계약', FIXED_TERM: '기간제 계약', CONTINGENT: '비정규 계약', INTERN: '인턴', OTHER: '기타' };
const contractStatusLabels: Record<string, string> = { ACTIVE: '유효', EXPIRED: '종료', TERMINATED: '해지', DRAFT: '작성 중' };
const compensationTypeLabels: Record<string, string> = { BASE_PAY: '기본급', BASE_SALARY: '기본급', HOURLY_RATE: '시급', ALLOWANCE: '수당' };
const payFrequencyLabels: Record<string, string> = { HOURLY: '시간 단위', WEEKLY: '주 지급', BIWEEKLY: '격주 지급', MONTHLY: '월 지급', ANNUAL: '연 지급', ONE_TIME: '일시 지급' };
const coverageLevelLabels: Record<string, string> = { EMPLOYEE: '본인', EMPLOYEE_SPOUSE: '본인·배우자', EMPLOYEE_CHILDREN: '본인·자녀', FAMILY: '가족', DEPENDENT_ONLY: '부양가족' };
const enrollmentStatusLabels: Record<string, string> = { ACTIVE: '가입', WAIVED: '미가입', PENDING: '처리 중', ENDED: '종료' };

function effectivePeriod(from: string, to?: string) {
  return `${from} ~ ${to || '현재'}`;
}

const bodySx = { p: `${designTokens.layout.sectionCardBodyPadding}px` };

export function EmployeeProfileRewards({ data }: { data: WorkerRewards }) {
  if (!data.contracts.length && !data.compensation.length && !data.benefitEnrollments.length) {
    return <SectionCard title="계약·보상"><EmptyState>등록된 계약·보상 정보가 없습니다.</EmptyState></SectionCard>;
  }
  return <Box data-testid="profile-employment-details"><PageSections>
    {data.contracts.length ? <SectionCard title="근로계약" headerPadding="standard"><Stack divider={<Divider />}>
      {data.contracts.map((contract) => <Box key={contract.id} sx={bodySx}>
        <DefinitionGrid columns={4} items={[
          { label: '계약 유형', value: <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}><ValueBadge label={contractTypeLabels[contract.contractType] || contract.contractType} /><StatusBadge label={contractStatusLabels[contract.contractStatus] || contract.contractStatus} tone={contract.contractStatus === 'ACTIVE' ? 'success' : contract.contractStatus === 'DRAFT' ? 'info' : 'neutral'} /></Stack> },
          { label: '계약 기간', value: effectivePeriod(contract.effectiveFrom, contract.effectiveTo) },
          { label: '체결일', value: profileDateValue(contract.signedOn) },
          { label: '주당 근무시간', value: profileTextValue(contract.weeklyHours) ? `${contract.weeklyHours}시간` : undefined },
        ]} />
      </Box>)}
    </Stack></SectionCard> : null}
    {data.compensation.length ? <SectionCard title="보상" headerPadding="standard"><Stack divider={<Divider />}>
      {data.compensation.map((compensation) => <Box key={compensation.id} sx={bodySx}>
        <DefinitionGrid columns={4} items={[
          { label: '보상 항목', value: <ValueBadge label={compensationTypeLabels[compensation.compensationType] || compensation.compensationType} /> },
          { label: '지급 주기', value: <ValueBadge label={payFrequencyLabels[compensation.payFrequency] || compensation.payFrequency} /> },
          { label: '금액', value: compensation.protected ? '금액은 별도 권한이 있을 때만 표시됩니다.' : profileTextValue(compensation.amount) ? `${compensation.amount} ${compensation.currencyCode}` : undefined },
          { label: '적용 기간', value: effectivePeriod(compensation.effectiveFrom, compensation.effectiveTo) },
        ]} />
      </Box>)}
    </Stack></SectionCard> : null}
    {data.benefitEnrollments.length ? <SectionCard title="가입한 복지" headerPadding="standard"><Stack divider={<Divider />}>
      {data.benefitEnrollments.map((enrollment) => <Box key={enrollment.id} sx={bodySx}>
        <DefinitionGrid columns={4} items={[
          { label: '복지 항목', value: enrollment.benefitPlan.descriptor || '복리후생 항목' },
          { label: '가입 상태', value: <StatusBadge label={enrollmentStatusLabels[enrollment.enrollmentStatus] || enrollment.enrollmentStatus} tone={enrollment.enrollmentStatus === 'ACTIVE' ? 'success' : enrollment.enrollmentStatus === 'PENDING' ? 'warning' : 'neutral'} /> },
          { label: '가입 범위', value: <ValueBadge label={coverageLevelLabels[enrollment.coverageLevel] || enrollment.coverageLevel} /> },
          { label: '가입 기간', value: effectivePeriod(enrollment.effectiveFrom, enrollment.effectiveTo) },
        ]} />
      </Box>)}
    </Stack></SectionCard> : null}
  </PageSections></Box>;
}
